import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import SEOHead from "@/components/SEOHead";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Calendar, Lock } from "lucide-react";

interface PublicGallery {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  event_date: string | null;
  cover_image_url: string | null;
}

const GalleriesIndex = () => {
  const [galleries, setGalleries] = useState<PublicGallery[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const { data } = await (supabase as any)
        .from("public_galleries")
        .select("id, slug, title, description, event_date, cover_image_url")
        .order("event_date", { ascending: false, nullsFirst: false });
      setGalleries(data || []);
      setLoading(false);
    };
    load();
  }, []);

  return (
    <>
      <SEOHead
        title="Galeries photo | Agence 8020"
        description="Découvrez les galeries photo de nos événements."
        canonical="/galeries"
      />
      <Navbar />
      <main className="min-h-screen bg-background pt-32 pb-20">
        <div className="container mx-auto px-4">
          <header className="mb-12 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Galeries photo</h1>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Sélectionnez votre événement et entrez le mot de passe qui vous a été remis.
            </p>
          </header>

          {loading ? (
            <p className="text-center text-muted-foreground">Chargement…</p>
          ) : galleries.length === 0 ? (
            <p className="text-center text-muted-foreground">Aucune galerie pour le moment.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {galleries.map((g) => (
                <Link key={g.id} to={`/galerie/${g.slug}`} className="group">
                  <Card className="overflow-hidden hover:shadow-xl transition-all">
                    <div className="aspect-[4/3] bg-muted relative overflow-hidden">
                      {g.cover_image_url ? (
                        <img
                          src={g.cover_image_url}
                          alt={g.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                          <Lock className="h-10 w-10" />
                        </div>
                      )}
                      <div className="absolute top-3 right-3 bg-background/80 backdrop-blur rounded-full p-2">
                        <Lock className="h-4 w-4" />
                      </div>
                    </div>
                    <div className="p-4">
                      <h2 className="font-semibold text-lg mb-1">{g.title}</h2>
                      {g.event_date && (
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(g.event_date).toLocaleDateString("fr-CA", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })}
                        </p>
                      )}
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
};

export default GalleriesIndex;
